import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DropdownOption } from '../../../shared/dropdown/Dropdown';
import { OverflowTooltip } from '../../../shared/tooltip';
import styles from './Theoretical.module.scss';
import { NettingEngineSurvey, NettingEngineSubQuestion, NettingEngineQuestionType, setSurveyQuestionIndex, getShowSummary, getLeiModalOpen, setLeiModalOpen, productsTradedOptions, setIsChangingQuestion, NettingEngineAgreement, ISDANettingEngineData } from '../store';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store';
import { nettingEngineAgreementOptions, counterpartyCoverageOptions } from '../Utils';
import { classifySoftSearchEntityStarted, getNettingEntities, Lei, searchEntitiesStarted } from '../../../admin/entity/store';
import { getAvailableSubCounterpartyTypes } from '../../../admin/sub-counterparty-types/store';
import { SearchEntityModal } from '../../../admin/entity/SearchEntityModal';
import { TheoreticalSurveyQuestions } from './TheoreticalSurveyQuestions';
import { TheoreticalSurveySlider } from './TheoreticalSurveySlider';
import { TheoreticalSurveyButtons } from './TheoreticalSurveyButtons';

interface TheoreticalSurveyProps {
    surveyQuestionIndex: number;
    surveyAnswers: ISDANettingEngineData;
}

export const TheoreticalSurvey: React.FC<TheoreticalSurveyProps> = ({
    surveyQuestionIndex,
    surveyAnswers
}) => {
    const { counterpartyNameKnown, counterparty, agreement, governingLaw, productTypesKnown, productsTraded, counterpartyIsMultiBranch, counterpartyOffices, paymentMethod } = surveyAnswers;

    const dispatch = useAppDispatch();

    const dropdownLists = useAppSelector(getAllDropdownLists);
    const showSummary = useAppSelector(getShowSummary);
    const allEntities = useAppSelector(getNettingEntities);
    const subCounterpartyList = useAppSelector(getAvailableSubCounterpartyTypes);
    const [selectedEntity, setSelectedEntity] = useState<Lei | null>(null);

    const setQuestionIndex = useCallback((index: number) => dispatch(setSurveyQuestionIndex(index)), [dispatch]);

    const getDropdownListOptions = useCallback((listName: string) => {
        const dropdownList = dropdownLists.find(({ name }) => name === listName);
        return dropdownList ? dropdownList.options.map(type => ({ value: type, label: type })) : [];
    }, [dropdownLists]);

    const jurisdictionOptions = useMemo(() => getDropdownListOptions('OpinionJurisdiction'), [getDropdownListOptions]);
    const cityOptions = useMemo(() => getDropdownListOptions('City'), [getDropdownListOptions]);
    const filteredCityOptions = cityOptions.filter(({ label }) => label !== 'Other');
    const [counterpartyOfficeOptions, setCounterpartyOfficeOptions] = useState(filteredCityOptions);
    const governingLawOptions = useMemo(() => getDropdownListOptions('GoverningLaw'), [getDropdownListOptions]);
    const subCounterpartyOptions = useMemo(() => subCounterpartyList.map(({ subCounterpartyName, subCounterpartyTypeId }) => ({ value: subCounterpartyTypeId!.toString(), label: subCounterpartyName })), [subCounterpartyList]);
    const entityOptions = allEntities.map(({ name, entityId }) => ({ value: entityId!.toString(), label: name }));
    const paymentMethodOptions = useMemo(() => getDropdownListOptions('PaymentMethod').filter(({ label }) => label !== 'Not applicable (2002 ISDA)'), [getDropdownListOptions]);

    const getDropdownValueForAnswer = useCallback((answer: string | null) => isNull(answer) ? null : { value: answer, label: answer }, []);
    const getCounterpartyAnswer = useCallback((entityId: string | null) => {
        if (!isNull(selectedEntity)) {
            return { value: selectedEntity.legalName, label: selectedEntity.legalName };
        } else {
            return isNull(entityId) ? null : { value: entityId, label: allEntities.find(entity => entity.entityId === parseInt(entityId))!.name };
        }
    }, [allEntities, selectedEntity]);
    const getSubCounterpartyAnswer = useCallback((subCounterpartyId: number | null) => {
        if (!isNull(subCounterpartyId)) {
            const answer = subCounterpartyOptions.find(({ value }) => value === subCounterpartyId.toString()) || null;
            return answer;
        }
        return null;
    }, [subCounterpartyOptions]);
    const getMultiDropdownValueForAnswer = useCallback((answer: string[] | null) => (isNull(answer) || answer.length === 0) ? null : answer.map(answer => ({ value: answer, label: answer })), []);

    const counterpartyQuestion: NettingEngineSurvey = useMemo(() => ({ question: 'What is the name of your counterparty?', type: NettingEngineQuestionType.DROPDOWN, dropdownOptions: entityOptions, value: getCounterpartyAnswer(counterparty.entityId), key: 'counterparty', counterpartyKey: 'entityId' }), [entityOptions, getCounterpartyAnswer, counterparty]);
    const counterpartySubQuestion: NettingEngineSubQuestion = useMemo(() => ({ question: 'What is the Jurisdiction of your counterparty?', value: getDropdownValueForAnswer(counterparty.jurisdiction), dropdownOptions: jurisdictionOptions, key: 'counterparty', counterpartyKey: 'jurisdiction' }), [counterparty, getDropdownValueForAnswer, jurisdictionOptions]);

    const productsTradedDropdownOptions: DropdownOption[] = useMemo(() => productsTradedOptions.map(value => ({ value, label: value })), []);
    const unknownCounterpartyQuestion: NettingEngineSurvey = useMemo(() => ({ question: 'What is the Jurisdiction of your counterparty?', type: NettingEngineQuestionType.DROPDOWN, value: getDropdownValueForAnswer(counterparty.jurisdiction), dropdownOptions: jurisdictionOptions, key: 'counterparty', counterpartyKey: 'jurisdiction' }), [counterparty, getDropdownValueForAnswer, jurisdictionOptions]);

    const subCounterpartyQuestion: NettingEngineSubQuestion = useMemo(() => ({ question: 'What is the sub classification of your counterparty?', value: getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId), dropdownOptions: subCounterpartyOptions, key: 'counterparty', counterpartyKey: 'subCounterpartyTypeId' }), [counterparty, subCounterpartyOptions, getSubCounterpartyAnswer]);

    const showPaymentMethodQuestion = useMemo(() => agreement === NettingEngineAgreement.ISDA_1992_MASTER, [agreement]);
    const paymentMethodQuestion: NettingEngineSurvey | null = useMemo(() => showPaymentMethodQuestion ? ({ question: 'What payment method will be selected for payment on early termination?', type: NettingEngineQuestionType.DROPDOWN, value: getDropdownValueForAnswer(paymentMethod || null), dropdownOptions: paymentMethodOptions, key: 'paymentMethod' }) : null, [paymentMethod, getDropdownValueForAnswer, paymentMethodOptions, showPaymentMethodQuestion]);

    const nettingEngineSurvey: NettingEngineSurvey[] = useMemo(() => [
        { question: 'Do you know the name of your counterparty?', type: NettingEngineQuestionType.BOOLEAN, value: counterpartyNameKnown, key: 'counterpartyNameKnown' },
        counterpartyNameKnown ? counterpartyQuestion : unknownCounterpartyQuestion,
        { question: 'What is the classification of your counterparty?', type: NettingEngineQuestionType.DROPDOWN, value: getDropdownValueForAnswer(counterparty.counterpartyClassification), dropdownOptions: counterpartyCoverageOptions, key: 'counterparty', counterpartyKey: 'counterpartyClassification' },
        { question: 'What agreement do you intend to put in place with your counterparty?', type: NettingEngineQuestionType.DROPDOWN, value: getDropdownValueForAnswer(agreement), dropdownOptions: nettingEngineAgreementOptions, key: 'agreement' },
        { question: 'What is the governing law of your agreement?', type: NettingEngineQuestionType.DROPDOWN, value: getDropdownValueForAnswer(governingLaw), dropdownOptions: governingLawOptions, key: 'governingLaw' },
        { question: 'Do you know which product types are to be traded under the relevant agreement?', type: NettingEngineQuestionType.BOOLEAN, value: productTypesKnown, key: 'productTypesKnown' },
        { question: 'Select product types to be traded', type: NettingEngineQuestionType.DROPDOWN, dropdownOptions: productsTradedDropdownOptions, value: getMultiDropdownValueForAnswer(productsTraded), key: 'productsTraded', isMulti: true, closeOnSelect: false },
        { question: 'Is your counterparty acting on a multi-branch basis?', type: NettingEngineQuestionType.BOOLEAN, value: counterpartyIsMultiBranch, key: 'counterpartyIsMultiBranch' },
        { question: 'Please provide a list of all of the offices through which your counterparty may act.', type: NettingEngineQuestionType.DROPDOWN, dropdownOptions: counterpartyOfficeOptions, value: getMultiDropdownValueForAnswer(counterpartyOffices), key: 'counterpartyOffices', isMulti: true, closeOnSelect: false },
    ], [counterpartyNameKnown, agreement, governingLaw, productTypesKnown, productsTraded, productsTradedDropdownOptions, counterpartyOfficeOptions, counterpartyIsMultiBranch, counterpartyOffices, getDropdownValueForAnswer, getMultiDropdownValueForAnswer, governingLawOptions, unknownCounterpartyQuestion, counterpartyQuestion, counterparty]);

    const getSummaryAnswer = useCallback((answer: boolean | DropdownOption | DropdownOption[] | null, type: NettingEngineQuestionType, isMulti?: boolean, question?: string) => {
        if (isNull(answer)) return '';
        if (type === NettingEngineQuestionType.BOOLEAN) return !answer ? 'No' : 'Yes';
        if (isMulti) return (answer as DropdownOption[]).map(({ label }) => label);
        if (question === 'What is the classification of your counterparty?') {
            return `${(answer as DropdownOption).label}${isNull(getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId)) ? '' : ` - ${getSubCounterpartyAnswer(counterparty.subCounterpartyTypeId)!.label}`}`;

        }
        return (answer as DropdownOption).label;
    }, [getSubCounterpartyAnswer, counterparty.subCounterpartyTypeId]);

    const leiModalOpen = useAppSelector(getLeiModalOpen);
    const closeEntityModal = useCallback(() => dispatch(setLeiModalOpen(false)), [dispatch]);
    const searchEntities = () => dispatch(searchEntitiesStarted());

    const softSaveEntity = useCallback((entity: Lei) => {
        setSelectedEntity(entity);
        dispatch(classifySoftSearchEntityStarted({
            name: entity.legalName,
            lei: entity.lei,
            city: entity.legalAddress.city,
            country: entity.legalAddress.country,
            jurisdiction: entity.jurisdiction
        }));
        setTimeout(() => {
            setQuestionIndex(surveyQuestionIndex + 1);
            dispatch(setIsChangingQuestion(false));
        }, 750);
    }, [dispatch, setQuestionIndex, surveyQuestionIndex]);

    const updateCounterpartyOfficeOptions = useCallback((options: DropdownOption[]) => setCounterpartyOfficeOptions(options), []);

    return (
        <div className={styles.survey}>
            <div className={styles.surveyWrapper}>
                {!showSummary &&
                    <>
                        <TheoreticalSurveySlider
                            surveyQuestionIndex={surveyQuestionIndex}
                            nettingEngineSurvey={nettingEngineSurvey}
                            setQuestionIndex={setQuestionIndex}
                        />
                        <TheoreticalSurveyQuestions
                            surveyQuestionIndex={surveyQuestionIndex}
                            nettingEngineSurvey={nettingEngineSurvey}
                            counterpartySubQuestion={counterpartySubQuestion}
                            subCounterpartyQuestion={subCounterpartyQuestion}
                            allEntities={allEntities}
                            setQuestionIndex={setQuestionIndex}
                            counterpartyNameKnown={counterpartyNameKnown}
                            jurisdictionOptions={jurisdictionOptions}
                            updateCounterpartyOfficeOptions={updateCounterpartyOfficeOptions}
                            paymentMethodQuestion={paymentMethodQuestion}
                        />
                    </>
                }
                {showSummary &&
                    <div className={styles.surveySummaryWrapper}>
                        <div className={styles.summaryTitle}>Summary</div>
                        {nettingEngineSurvey.map(({ question, value, type, isMulti }, index) => {
                            if (isNull(value)) {
                                return null;
                            }
                            return (
                                <div className={styles.surveySummary} key={index}>
                                    <OverflowTooltip className={styles.surveySummaryQuestion} overlayText={`Q: ${question}`} />
                                    <OverflowTooltip className={styles.surveySummaryAnswer} overlayText={`A: ${getSummaryAnswer(value, type, isMulti, question)}`} />
                                </div>
                            );
                        })}
                    </div>
                }
            </div>
            <TheoreticalSurveyButtons
                surveyQuestionIndex={surveyQuestionIndex}
                nettingEngineSurvey={nettingEngineSurvey}
                setQuestionIndex={setQuestionIndex}
            />
            <SearchEntityModal
                searchEntities={searchEntities}
                closeModal={closeEntityModal}
                isOpen={leiModalOpen}
                softSaveEntity={softSaveEntity}
                selectedEntity={selectedEntity}
            />
        </div>
    );
};
